import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import { Box, Stack, Typography } from '@mui/material';

import type { IssueDetailItem, IssueDetailModel } from './issueDetailModel';

/**
 * The Issues popover's content (GDN-3379): the cell's own count phrase on
 * top, the other non-zero counts beside it, then every issue as a severity
 * icon, a title, the reason underneath and the fix when the producer gave
 * one. Presentational: the list arrives built, and the read behind it lives
 * in `IssueDetailContainer`.
 */
export interface IssueDetailProps {
  /** The cell's own count phrase, so the popover leads with the words the biller hovered. */
  headline: string;
  model: IssueDetailModel;
  /** The certification is still being read; the row's own items show meanwhile. */
  loading?: boolean;
}

const LINE_SX = { fontSize: 'inherit', lineHeight: 'inherit' } as const;

const IssueLine = ({ item }: { item: IssueDetailItem }) => (
  <Stack direction="row" spacing={0.75} sx={{ alignItems: 'flex-start' }}>
    <ErrorOutlineIcon
      color={item.blocking ? 'error' : 'warning'}
      sx={{ flexShrink: 0, fontSize: 16, mt: '2px' }}
    />
    <Box sx={{ minWidth: 0 }}>
      <Typography component="div" sx={{ ...LINE_SX, fontWeight: 600 }}>
        {item.title}
      </Typography>
      {item.why && (
        <Typography color="text.secondary" component="div" sx={LINE_SX}>
          {item.why}
        </Typography>
      )}
      {item.remediation && (
        <Typography color="text.secondary" component="div" sx={LINE_SX}>
          {`Fix: ${item.remediation}`}
        </Typography>
      )}
    </Box>
  </Stack>
);

export const IssueDetail = ({ headline, model, loading = false }: IssueDetailProps) => {
  /* The headline already says one of the counts; the rest follow it. */
  const others = model.counts.filter(count => count !== headline);
  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      <Typography component="div" sx={{ ...LINE_SX, fontWeight: 600 }}>
        {headline}
        {others.length > 0 && (
          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
            {` · ${others.join(' · ')}`}
          </Box>
        )}
      </Typography>
      {model.items.map(item => (
        <IssueLine key={item.key} item={item} />
      ))}
      {model.more > 0 && (
        <Typography color="text.secondary" component="div" sx={LINE_SX}>
          {`and ${model.more} more`}
        </Typography>
      )}
      {loading && (
        <Typography color="text.secondary" component="div" sx={LINE_SX}>
          Loading the certification…
        </Typography>
      )}
    </Stack>
  );
};
